import { isEmpty } from "ra-core";
import { SCREENING_REPORT_TYPES } from "../components/Constants";
import { getOnlyDateComponent, getParsedDate, parseDateForExport } from "../utils/util";
import { isAdminUser } from "../components/DataProvider";

export const parseIdReportsData = (data) => {
  if (!data) return {};

  const {
    id,
    createdAt,
    updatedAt,
    identityRequestStatus,        
    clientId,
    screeningReportDtoList = [],
    userId,
    continuousMonitoringEnabled,
    requestingUserId,
    screeningMatchStatus,
    daysToRenewal,
    cmStartedAt,
    cmFinishedAt,
  } = data;
  
  const { userData = {}, clientRequestId } = data?.reportRequestParamsJson !== null ? JSON.parse(data?.reportRequestParamsJson) : {};
  const lastModified = getParsedDate(updatedAt);
  const created = getParsedDate(createdAt);
  const name = userData?.accountName;
  const emailAddress = userData?.emailAddress;
  const pepReport = getReportData(screeningReportDtoList, SCREENING_REPORT_TYPES.POLITICALLY_EXPOSED_PERSON);
  const watchlistReport = getReportData(screeningReportDtoList, SCREENING_REPORT_TYPES.WATCHLIST);
  const adverseMediaReport = getReportData(screeningReportDtoList, SCREENING_REPORT_TYPES.ADVERSE_MEDIA);

  return {
    id : isAdminUser() ? 'client/' + clientId + '/identity-verification/' + id : id,    
    name,
    emailAddress,
    createdAt: created,
    updatedAt: lastModified,
    identityRequestStatus,
    clientRequestId,
    clientId,
    userId,
    continuousMonitoringEnabled,
    requestingUserId,
    screeningMatchStatus,
    pepReportId: pepReport.id,
    watchlistReportId: watchlistReport.id,
    adverseMediaReportId: adverseMediaReport.id,
    pepReportMatch: pepReport.screeningMatchStatus,
    watchlistReportMatch: watchlistReport.screeningMatchStatus,
    adverseMediaReportMatch: adverseMediaReport.screeningMatchStatus,
    daysToRenewal,
    cmStartedAt : getOnlyDateComponent(cmStartedAt),
    cmFinishedAt : getOnlyDateComponent(cmFinishedAt),
  };
};

export const parseIdRequest = (data) => {
  if (!data) return {};

  const { 
    id, 
    identityRequestStatus, 
    createdAt, 
    updatedAt,
    reportRequestParamsJson, 
    clientId, 
    screeningReportDtoList = [],
    continuousMonitoringEnabled, 
    screeningMatchStatus,
    daysToRenewal,
    cmStartedAt,
    cmFinishedAt, 
  } = data;
    
  const created = getParsedDate(createdAt);
  const lastModified = getParsedDate(updatedAt);
  const requestParams = JSON.parse(reportRequestParamsJson);
  const templateId = requestParams?.reportParams?.identityVerificationTemplateId;
  const name = requestParams?.userData?.accountName;
  const emailAddress = requestParams?.userData?.emailAddress;
  const { clientRequestId } = requestParams || {};

  const pepReport = getReportData(screeningReportDtoList, SCREENING_REPORT_TYPES.POLITICALLY_EXPOSED_PERSON);
  const watchlistReport = getReportData(screeningReportDtoList, SCREENING_REPORT_TYPES.WATCHLIST);
  const adverseMediaReport = getReportData(screeningReportDtoList, SCREENING_REPORT_TYPES.ADVERSE_MEDIA);

  return {
    id : isAdminUser() ? 'client/' + clientId + '/identity-verification/' + id : id,
    identityRequestStatus,
    created,
    lastModified,
    templateId,
    name,
    emailAddress,
    clientId,
    clientRequestId,
    pepReportId: pepReport.id,
    watchlistReportId: watchlistReport.id,
    adverseMediaReportId: adverseMediaReport.id,
    pepReportMatch: pepReport.screeningMatchStatus,
    watchlistReportMatch: watchlistReport.screeningMatchStatus,
    adverseMediaReportMatch: adverseMediaReport.screeningMatchStatus,
    continuousMonitoringEnabled,
    screeningMatchStatus,
    daysToRenewal,
    cmStartedAt : getOnlyDateComponent(cmStartedAt),
    cmFinishedAt : getOnlyDateComponent(cmFinishedAt),
  };
}

export const parseScreeningReport = (data) => {  
  if (!data) return {};
  
  const { 
    id, 
    continuousMonitoringEnabled, 
    reportType, 
    updatedAt, 
    events = [],
    createdAt,
    screeningMatchStatus,
    continuousMonitoringStartedAt,
    continuousMonitoringStoppedAt,
    daysToRenewal,
  } = data;

  return {
    id,
    reportType,
    continuousMonitoringEnabled,
    lastModified: getParsedDate(updatedAt),
    created: getParsedDate(createdAt),
    events: parseEvents(events),
    screeningMatchStatus,
    daysToRenewal : daysToRenewal,
    cmStartedAt : getOnlyDateComponent(continuousMonitoringStartedAt),
    cmFinishedAt : getOnlyDateComponent(continuousMonitoringStoppedAt),
  };
}

const parseEvents = (events) => {
  if (!Array.isArray(events)) return [];
  
  return events.map(event => {
    const { 
      id, 
      birthRecord, 
      name, 
      reviewedBy, 
      reviewedAt, 
      screeningReportId, 
      sources, 
      status, 
      summaries, 
      createdAt, 
      updatedAt,
      reviewReason, 
    } = event;

    return {
      id,
      birthRecord: !isEmpty(birthRecord) ? birthRecord : "",
      name,
      reviewedBy,
      reviewedAt: !isEmpty(reviewedAt) ? getParsedDate(reviewedAt) : "",
      screeningReportId,
      sources,
      status,
      summaries,
      created: getParsedDate(createdAt),
      lastModified: getParsedDate(updatedAt),
      reviewReason,
    };
  });
}

const getReportData = (screeningReportDtoList, reportType) => {
  if (!Array.isArray(screeningReportDtoList)) return [];
  const report = screeningReportDtoList.filter(report => report.reportType === reportType)?.[0];
  return {
    id: report?.id,
    screeningMatchStatus: report?.screeningMatchStatus,
  };
};

export const parseIdRecordsforExport = (records, data) => {  
  return records.map((record) => {             
      return parseIdReportForExport(record, data);
  })
}

const parseIdReportForExport = ( record, reqUserName ) => {

  const {
    id,
    clientRequestId,
    name,
    emailAddress,
    continuousMonitoringEnabled,
    screeningMatchStatus,
    hasUnAcknowledgedMatch,
    pepReportId,
    pepReportMatch,
    watchlistReportId,
    watchlistReportMatch,
    adverseMediaReportId,
    adverseMediaReportMatch,
    daysToRenewal,
    cmStartedAt,
    cmFinishedAt,
    createdAt,
    updatedAt,
    requestingUserId,
  } = record;        

  const data = {
    id,
    clientRequestId,        
    name,
    emailAddress,
    hasUnAcknowledgedMatch,
    screeningMatchStatus,
    pepReportId,
    pepReportMatch,
    watchlistReportId,
    watchlistReportMatch,
    adverseMediaReportId,
    adverseMediaReportMatch,    
    continuousMonitoringEnabled,
    daysToRenewal,
    cmStartedAt : getOnlyDateComponent(cmStartedAt),
    cmFinishedAt : getOnlyDateComponent(cmFinishedAt),
    created : parseDateForExport(createdAt),
    lastModified : parseDateForExport(updatedAt),
    requestingUserName : reqUserName && reqUserName.get(requestingUserId) != null ? reqUserName.get(requestingUserId) : ' ',        
  };

  return data;
}